import { ChannelType } from './email-channel.model';

export type CampaignStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'CANCELLED'
  | 'FAILED';

export type CampaignChannelType = ChannelType | 'SYSTEM_API';

/**
 * Diccionario único de estados de campaña del módulo de Correo marketing.
 * Ningún componente debe volver a declarar este mapa ni pintar la llave cruda.
 */
export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Borrador',
  SCHEDULED: 'Programado',
  SENDING: 'Enviando',
  SENT: 'Enviado',
  CANCELLED: 'Cancelado',
  FAILED: 'Falló',
};

export function campaignStatusLabel(status: CampaignStatus | string | null | undefined): string {
  if (!status) return '—';
  return CAMPAIGN_STATUS_LABELS[status as CampaignStatus] ?? String(status);
}

/**
 * Adjunto de una campaña. En el request, `storageRef` (string del fileId del
 * upload) y `fileName` son obligatorios; `mimeType`/`sizeBytes` son opcionales
 * pero recomendados. En el response (detalle de campaña) vienen poblados.
 */
export interface CampaignAttachment {
  fileName: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  storageRef: string;
}

export interface MarketingCampaign {
  id: number;
  businessId: number;
  idUserOwner: number;
  name: string;
  subject?: string | null;
  preheader?: string | null;
  bodyHtml?: string | null;
  bodyText?: string | null;
  templateCode?: string | null;
  status: CampaignStatus;
  scheduledAt?: string | null;
  sentAt?: string | null;
  channelType?: CampaignChannelType | null;
  channelRefId?: number | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  /**
   * Destinatarios reales de la campaña. En DRAFT/SCHEDULED es un estimado
   * (segmentos + manuales); poblado también en Borrador/Programado.
   */
  recipientCount?: number;
  /** Encolados (aún sin salir al proveedor). */
  queuedCount?: number;
  /** Entregados (aceptados por el proveedor) — usar para el badge "Entregado". */
  deliveredCount?: number;
  /** Rebotados (bounce). */
  bouncedCount?: number;
  /** Aperturas únicas (para derivar tasa de apertura en el listado). */
  openedCount?: number;
  lastError?: string | null;
  folderId?: number | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Adjuntos de la campaña. Poblado en el detalle (getById/create/update/cancel).
   * OJO: el LISTADO (GET /api/marketing/campaigns) lo devuelve `null` por
   * performance — solo confiar en el detalle.
   */
  attachments?: CampaignAttachment[] | null;
}

export interface MarketingCampaignRequest {
  name: string;
  subject?: string | null;
  preheader?: string | null;
  bodyHtml?: string | null;
  bodyText?: string | null;
  templateCode?: string | null;
  channelType?: CampaignChannelType | null;
  channelRefId?: number | null;
  scheduledAt?: string | null;
  folderId?: number | null;
  /**
   * Adjuntos (REPLACE-ALL): omitido/undefined → no toca los actuales (update
   * parcial); [] → borra todos; [items] → reemplaza por exactamente esos.
   */
  attachments?: CampaignAttachment[];
}

export type CampaignListSourceType = 'STATIC' | 'DYNAMIC';

export interface CampaignList {
  id: number;
  idMarketingCampaign: number;
  idContactSegment: number;
  sourceType: CampaignListSourceType;
  createdAt: string;
}

export interface AddCampaignListRequest {
  segmentId: number;
  sourceType: CampaignListSourceType;
}

export interface SendNowSummary {
  campaignId: number;
  status: CampaignStatus;
  totalRecipients: number;
  queued: number;
  failed: number;
  skipped: number;
}

// ===== Métricas agregadas (dashboard) =====
// El backend (GET /api/marketing/campaigns/{id}/metrics) devuelve la tasa con
// nombre distinto por bloque: `openRate` en opens y `clickRate` en clicks.
// (Antes el FE esperaba `rate` → Open/Click Rate salían siempre "—". Inc 32.)
export interface CampaignOpenMetric {
  unique: number;
  total: number;
  /** aperturas únicas / entregados. null si delivered = 0 */
  openRate: number | null;
}

export interface CampaignClickMetric {
  unique: number;
  total: number;
  /** clicks únicos / entregados. null si delivered = 0 */
  clickRate: number | null;
  /** click-to-open: clicks.unique / opens.unique. null si opens = 0 */
  ctor?: number | null;
}

export interface CampaignMetrics {
  campaignId: number;
  status: CampaignStatus;
  totalRecipients: number;
  /** Recipient status breakdown */
  pending: number;
  queued: number;
  sent: number;
  failed: number;
  skipped: number;
  /** Sub-desglose del skipped por motivo de exclusión */
  excludedByCampaign?: number;
  excludedBySuppression?: number;
  /** Email message status (provider-reported) */
  delivered: number;
  bounced: number;
  complained: number;
  opens: CampaignOpenMetric;
  clicks: CampaignClickMetric;
  lastEventAt?: string | null;
}

// ===== Recipients drilldown =====
export type CampaignRecipientStatus =
  | 'PENDING'
  | 'QUEUED'
  | 'SENT'
  | 'FAILED'
  | 'SKIPPED';

export type EmailMessageStatus =
  | 'QUEUED'
  | 'SENDING'
  | 'SENT'
  | 'DELIVERED'
  | 'OPENED'
  | 'CLICKED'
  | 'BOUNCED'
  | 'COMPLAINED'
  | 'FAILED';

export interface CampaignRecipient {
  id: number;
  idContact: number;
  email: string;
  fullName: string;
  recipientStatus: CampaignRecipientStatus;
  recipientError?: string | null;
  idEmailMessage?: number | null;
  emailStatus?: EmailMessageStatus | null;
  deliveredAt?: string | null;
  firstOpenedAt?: string | null;
  lastOpenedAt?: string | null;
  openCount?: number;
  clickCount?: number;
  emailError?: string | null;
}

export interface CampaignRecipientsParams {
  status?: CampaignRecipientStatus;
  page?: number;
  size?: number;
}

// ===== Manual recipients =====
export interface CampaignManualRecipient {
  id: number;
  /** null para emails sueltos (no registrados como contacto). */
  idContact: number | null;
  fullName: string;
  email: string;
  addedAt: string;
  addedByUserId: number;
}

/** Un email arbitrario para agregar como destinatario directo (Inc 27). */
export interface ManualEmailRecipient {
  email: string;
  fullName?: string;
}

/** Body de POST /manual-recipients/emails. */
export interface AddManualEmailsRequest {
  recipients: ManualEmailRecipient[];
}

// ===== Excluded contacts / segments =====
export interface CampaignExcludedContact {
  id: number;
  idContact: number;
  fullName: string;
  email: string;
  reason?: string | null;
  excludedAt: string;
  excludedByUserId: number;
}

export interface CampaignExcludedSegment {
  id: number;
  idMarketingCampaign: number;
  idContactSegment: number;
  sourceType: CampaignListSourceType;
  createdAt: string;
}

export interface AddExcludedSegmentRequest {
  segmentId: number;
  sourceType: CampaignListSourceType;
}

// ===== Batch operations =====
export interface BatchContactsRequest {
  contactIds: number[];
}

export interface BatchAddSummary {
  added: number;
  skipped: number;
  alreadyPresent: number;
}

// ===== Preview de destinatarios resueltos =====
export interface CampaignPreviewCount {
  included: number;
  excluded: number;
  finalEstimate: number;
}
